import * as React from "react";

const stores = {};
export function registerStore(name: string, store: any) {
	stores[name] = store;
}
const actions = {};
export function registerActions(name: string, actionObject: Record<string, any>) {
	actions[name] = actionObject;
}

function handleStore(handler: (arg0: any, arg1: Record<string, any>) => Record<string, any> | string[], store, props) {
	if (typeof handler === "function") {
		return handler(store, props);
	} else if (Array.isArray(handler) && typeof store.getInitialState === "function") {
		return Object.entries(store.getInitialState()).reduce(
			(acc, [key, value]) => ((handler as string).includes(key) ? { ...acc, [key]: value } : acc),
			{},
		);
	} else {
		return {};
	}
}

function handleActions(handler, actionObject) {
	if (Array.isArray(handler)) {
		return Object.entries(actionObject).reduce(
			(acc, [key, value]) => (handler.includes(key) ? { ...acc, [key]: value } : acc),
			{},
		);
	} else {
		return {};
	}
}

type StoreSpec = {
	stores?: Record<string, any>;
	actions?: Record<string, any>;
};

function checkSpec(spec: StoreSpec) {
	let bad = false;
	const knownOptions = ["stores", "actions"];
	const unknownOptions = Object.keys(spec).filter(option => !knownOptions.includes(option));

	if (unknownOptions.length > 0) {
		bad = true;
		console.error("Bad connect spec: unknown options:", unknownOptions);
	}

	Object.entries(spec.stores ?? {}).forEach(([key, handler]) => {
		if (!stores[key]) {
			bad = true;
			console.error("Bad connect spec: unknown store:", key);
		}

		if (typeof handler !== "function" && !Array.isArray(handler)) {
			bad = true;
			console.error("Bad connect spec: bad store handler:", key);
		}
	});
	Object.entries(spec.actions ?? {}).forEach(([key, handler]) => {
		if (!actions[key]) {
			bad = true;
			console.error("Bad connect spec: unknown action:", key);
		}

		if (!Array.isArray(handler)) {
			bad = true;
			console.error("Bad connect spec: bad action handler:", key);
		}
	});

	if (bad) {
		throw new Error("Bad connect spec");
	}
}

export function connect<P>(spec: StoreSpec, Component: React.ComponentType<P>): React.ComponentType<P> {
	checkSpec(spec);
	const displayName = Component.displayName ?? (typeof Component === "function" ? Component.name : "");
	class Wrapper extends React.Component<P> {
		static displayName = displayName + "Wrapper";
		unsubscribeFunctions: (() => void)[] = [];

		componentDidMount() {
			Object.keys(spec.stores ?? {}).forEach(key => {
				const store = stores[key];
				// We just get the data directly from the stores without
				// saving it in state, so here we just do a forceUpdate. If
				// we want to do something more advanced here, it would be
				// easy to switch though.
				this.unsubscribeFunctions.push(store.listen(() => this.forceUpdate(), this));
			});
		}

		componentWillUnmount() {
			this.unsubscribeFunctions.forEach(fn => fn());
		}

		render() {
			const props = {
				...Object.entries(spec.stores ?? {}).reduce(
					(acc, [storeKey, handler]) => ({
						...acc,
						...handleStore(handler, stores[storeKey], this.props),
					}),
					{},
				),
				...Object.entries(spec.actions ?? {}).reduce(
					(acc, [actionKey, handler]) => ({ ...acc, ...handleActions(handler, actions[actionKey]) }),
					{},
				),
				...this.props,
			};
			return <Component {...props} />;
		}
	}
	return Wrapper;
}
